import { ADMIN_ROLES, type RoleCode } from "@seagames/contracts";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../components/page/page-header";
import { FeaturedStreamCard } from "../../components/streams/featured-stream-card";
import { featuredStreamsQuery } from "../../components/streams/featured-queries";
import { Button } from "../../components/ui/button";
import { EmptyState } from "../../components/ui/empty-state";
import { Spinner } from "../../components/ui/spinner";
import { errorMessage } from "../../lib/api/messages";

/**
 * Setup › Featured video (live video, 0086): one card per games (SEA, Para)
 * with the video that plays first on the portal's home page while it is on —
 * ceremonies, the studio show. Opened by the ACTIVE role: SUPER_ADMIN edits,
 * ADMIN reads (the API's own fences, `/featured-streams`); anyone else is
 * told so and nothing is fetched.
 */
export const Route = createFileRoute("/_app/featured-video")({
	component: FeaturedVideoPage,
});

const MAY_READ = (role: RoleCode | null) => role !== null && (ADMIN_ROLES as readonly RoleCode[]).includes(role);

function FeaturedVideoPage() {
	const { t } = useTranslation("streams");
	const { session } = Route.useRouteContext();
	const allowed = MAY_READ(session.active_role);
	const canWrite = session.active_role === "SUPER_ADMIN";
	const list = useQuery({ ...featuredStreamsQuery(), enabled: allowed });

	if (!allowed) {
		return (
			<div className="flex flex-col">
				<PageHeader title={t("featured.title")} />
				<EmptyState title={t("featured.forbiddenTitle")} body={t("featured.forbiddenBody")} />
			</div>
		);
	}

	return (
		<div className="flex flex-col">
			<PageHeader
				title={t("featured.title")}
				subtitle={
					<>
						{t("featured.subtitle")}
						{!canWrite && <span className="mt-[0.25rem] block">{t("featured.readOnly")}</span>}
					</>
				}
			/>
			{list.isPending ? (
				<Spinner className="size-[1.5rem] text-board" label={t("featured.loading")} />
			) : list.isError ? (
				<EmptyState
					title={t("featured.loadError")}
					body={errorMessage(list.error)}
					action={
						<Button variant="outline" size="sm" onClick={() => void list.refetch()}>
							{t("featured.retry")}
						</Button>
					}
				/>
			) : list.data.length === 0 ? (
				<EmptyState title={t("featured.empty")} />
			) : (
				<div className="grid items-start gap-[1rem] xl:grid-cols-2">
					{list.data.map((g) => (
						<FeaturedStreamCard key={g.games_id} games={g} canWrite={canWrite} />
					))}
				</div>
			)}
		</div>
	);
}
